import { gql } from '@apollo/client/core'
import { AudienceCriterionState } from '../../../gql-gen/questionnaire/graphql'
import { MemberSettingCode } from '../../../model/audience'
import { FieldworkAudienceType } from '../../../model/audience'

export interface MarketAudienceMemberIds {
  memberType: FieldworkAudienceType
  memberItem: {
    __typename?: string
    // Custom
    questionLk?: string
    state?: AudienceCriterionState
    criterionOptions?: { responseOptionLk: string }[]
    // Standard
    criterion?: { code: string; name: string; question: { questionId: string } }
    standardCriterionOptions?: {
      criterionOption: { responseOption: { responseOptionId: string } }
    }[]
    customizedCriterionOptions?:
      | {
          criterionOption: { responseOption: { responseOptionId: string } }
        }[]
      | null
    settingValues?: { code: MemberSettingCode; value: string }[]
  }
}

export interface MarketAudienceIdsData {
  questionnaireAggV2: {
    marketsSpecification?:
      | {
          key: string
          value: {
            draftAudience: { members: MarketAudienceMemberIds[] }
          }
        }[]
      | null
  } | null
}

/**
 * Every market's audience question/response-option ids in one round trip.
 * `questionnaireAggV2` is a project-level aggregate reachable from any of the
 * project's survey ids and returns `marketsSpecification` keyed by market, so
 * no client-side market enumeration (or query per market) is needed.
 *
 * Deliberately a minimal projection rather than the DRAFT_AUDIENCE fragment:
 * ids, the settings that decide which options are effective, and the criterion's
 * catalogue name — which the pickers label standard criteria with, because the
 * question text they would otherwise show is the base market's own language.
 * Run it
 * with `fetchPolicy: 'no-cache'` — audience entities are normalised by ids that
 * are identical across markets, so caching several markets' audiences would
 * collide with the audience page's cache (see AudiencesPage's cache eviction).
 */
export const GET_MARKET_AUDIENCE_IDS = gql`
  query marketAudienceIdsV2($surveyId: ID!) {
    questionnaireAggV2(surveyId: $surveyId) {
      marketsSpecification {
        key
        value {
          draftAudience {
            members {
              memberType
              memberItem {
                __typename
                ... on DraftCustomAudienceItem {
                  questionLk
                  state
                  criterionOptions {
                    responseOptionLk
                  }
                }
                ... on DraftStandardAudienceItem {
                  criterion {
                    code
                    name
                    question {
                      questionId
                    }
                  }
                  settingValues {
                    code
                    value
                  }
                  standardCriterionOptions {
                    criterionOption {
                      responseOption {
                        responseOptionId
                      }
                    }
                  }
                  customizedCriterionOptions {
                    criterionOption {
                      responseOption {
                        responseOptionId
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`
